// Team play — engine side (docs/team-games-plan.md §4–§5).
//
// The pure predicates (`teamOf`, `areAllies`, `canPlaceOn`, …) live in
// @warcarp/api-types so the app shares them; this module adds what needs the
// engine state or the RNG: who is still in, the sides still in, who won, team
// assignment and the interleaved turn order.
//
// Every helper is gated on `state.teams`. Absent means free-for-all, and each
// helper then returns exactly what the free-for-all code computed before teams
// existed (docs/team-games-plan.md §2).

import {
  TEAM_IDS,
  activeTeams as sharedActiveTeams,
  resignedSlotIds,
  teamForSlot,
  teamMembers,
  teamOf,
  type TeamsConfig,
} from '@warcarp/api-types'
import type { GameEvent, GameState, PlayerId, TeamId, TeamMembership } from './types.js'
import type { Rng } from './dice.js'
import { shuffle } from './cards.js'

// ─── Who is still in ────────────────────────────────────────────────────────

/**
 * Players still in the game, in turn order: not eliminated (or resigned) and
 * holding at least one territory. The one definition behind every "last one
 * standing" check.
 */
export function activePlayers(state: GameState): PlayerId[] {
  return state.players.filter(
    (p) => !state.eliminated.includes(p) && state.map.territories.some((t) => t.ownerId === p),
  )
}

/**
 * The sides still in the game: teams in a team game, players in free-for-all
 * (in turn order of their first active member). One side left means the game
 * is over.
 */
export function activeTeams(state: GameState): TeamId[] | PlayerId[] {
  return sharedActiveTeams(state, activePlayers(state))
}

/** A player's side: their team, or the player themselves in free-for-all. */
export function sideOf(state: GameState, playerId: PlayerId): string {
  return teamOf(state, playerId) ?? playerId
}

/**
 * Everyone who wins when `side` wins: every member of the team in turn order
 * (members eliminated earlier included, plan §1 rule 4), or the lone player in
 * free-for-all.
 */
export function sideMembers(state: GameState, side: string): PlayerId[] {
  if (!state.teams) return [side]
  const members = teamMembers(state, side as TeamId)
  return members.length > 0 ? members : [side]
}

/**
 * Everyone who shares the win when `side` wins: {@link sideMembers} minus the
 * members who resigned. A resigned player records a loss even when their
 * team wins; members eliminated in battle still share the win (plan §12
 * decision 7, maintainer 2026-11-01). Free-for-all: the lone player.
 */
export function sideWinners(state: GameState, side: string): PlayerId[] {
  const members = sideMembers(state, side)
  if (!state.teams) return members
  const resigned = resignedSlotIds(state)
  return members.filter((p) => !resigned.has(p))
}

/**
 * The winner when one side is left standing: the first winner of the first
 * active side, in turn order (a team's resigned members never win). In
 * free-for-all that is the first active player, exactly what the old
 * `players.find(active)` lookups returned. Undefined when nobody is left.
 */
export function survivingWinner(state: GameState): PlayerId | undefined {
  const first = activePlayers(state)[0]
  if (first === undefined) return undefined
  return sideWinners(state, sideOf(state, first))[0] ?? first
}

/**
 * The team fields of a result with `winner` (the first winner in turn order,
 * or null on a tie / no winner): `winnerTeam` and `winners`, every member of
 * the winning team except those who resigned ({@link sideWinners}). Empty in
 * free-for-all so FFA results and `game-end` events are unchanged.
 */
export function teamResultFor(
  state: GameState,
  winner: PlayerId | null,
): { winnerTeam?: TeamId | null; winners?: PlayerId[] } {
  if (!state.teams) return {}
  if (winner === null) return { winnerTeam: null, winners: [] }
  const team = teamOf(state, winner)
  return { winnerTeam: team, winners: team ? sideWinners(state, team) : [winner] }
}

/**
 * `team-eliminated` events for the teams that had a member in play before and
 * have none after. `lastPlayer` is the member whose exit finished the team:
 * the last of that team in `newlyOut`. Empty in free-for-all.
 */
export function teamEliminationEvents(
  before: GameState,
  after: GameState,
  newlyOut: readonly PlayerId[],
  ts: number,
): GameEvent[] {
  if (!before.teams) return []
  const stillIn = new Set<string>(activeTeams(after))
  const events: GameEvent[] = []
  const seen = new Set<TeamId>()
  for (let i = newlyOut.length - 1; i >= 0; i--) {
    const pid = newlyOut[i]!
    const team = teamOf(before, pid)
    if (team === null || seen.has(team) || stillIn.has(team)) continue
    seen.add(team)
    events.unshift({ kind: 'team-eliminated', team, lastPlayer: pid, ts })
  }
  return events
}

// ─── Game start ─────────────────────────────────────────────────────────────

/**
 * Assign every player a team at game start. `playerIds` are slot strings.
 *   - `manual`: slot-derived (`teamForSlot`), so seats 1..size are Team A.
 *   - `random`: the seeded RNG shuffles the players and deals them into
 *     teams of `size`, Team A first.
 * Throws when the players don't form at least two equal, full teams: team
 * games only start when every seat is filled (plan §7), and the server
 * validates that before it gets here.
 */
export function assignTeams(playerIds: readonly PlayerId[], format: TeamsConfig, rng: Rng): TeamMembership {
  const { size, assignment } = format
  const teamCount = playerIds.length / size
  if (!Number.isInteger(teamCount) || teamCount < 2 || teamCount > TEAM_IDS.length) {
    throw new Error(`assignTeams: ${playerIds.length} players cannot form equal teams of ${size}`)
  }
  const teams: TeamMembership = {}
  if (assignment === 'random') {
    shuffle(playerIds, rng).forEach((pid, i) => { teams[pid] = TEAM_IDS[Math.floor(i / size)]! })
    return teams
  }
  for (const pid of playerIds) {
    const team = teamForSlot(Number(pid), size)
    if (team === null) throw new Error(`assignTeams: seat ${pid} has no team for teams of ${size}`)
    teams[pid] = team
  }
  const counts = Object.values(teams).reduce<Record<string, number>>((acc, t) => ({ ...acc, [t]: (acc[t] ?? 0) + 1 }), {})
  if (Object.keys(counts).length !== teamCount || Object.values(counts).some((n) => n !== size)) {
    throw new Error('assignTeams: manual seats do not form full teams')
  }
  return teams
}

/**
 * The opening turn order for a team game (plan §4): shuffle the teams, shuffle
 * the members of each team, then go round-robin — member 1 of each team in
 * team order, then member 2, and so on. With teams A and B: A1 B1 A2 B2.
 * Players without a team (never the case after `assignTeams`) are appended in
 * the order given.
 */
export function buildTurnOrder(playerIds: readonly PlayerId[], teams: TeamMembership, rng: Rng): PlayerId[] {
  const present = TEAM_IDS.filter((team) => playerIds.some((p) => teams[p] === team))
  const teamOrder = shuffle(present, rng)
  const rosters = teamOrder.map((team) => shuffle(playerIds.filter((p) => teams[p] === team), rng))
  const order: PlayerId[] = []
  const longest = Math.max(0, ...rosters.map((r) => r.length))
  for (let i = 0; i < longest; i++) {
    for (const roster of rosters) {
      const pid = roster[i]
      if (pid !== undefined) order.push(pid)
    }
  }
  for (const p of playerIds) if (teams[p] === undefined) order.push(p)
  return order
}
